<template>
  <div class="section-card">
    <!-- 顶部工具栏 -->
    <div class="toolbar">
      <div class="toolbar-row">
        <div class="toolbar-left">
          <span class="title">主播列表</span>
          <span class="count-badge">{{ totalCount }}位主播</span>
        </div>
        <div class="online-filter" :class="{ on: onlyOnline }">
          <span class="of-dot"></span>
          <span class="of-label">仅显示在线</span>
          <NSwitch v-model:value="onlyOnline" size="small" />
        </div>
      </div>
      <div class="toolbar-row">
        <div class="sort-group">
          <NButton size="tiny" :type="sortBy === 'active' ? 'primary' : 'default'" @click="emit('update:sortBy', 'active')" ghost>最近活跃</NButton>
          <NButton size="tiny" :type="sortBy === 'credit' ? 'primary' : 'default'" @click="emit('update:sortBy', 'credit')" ghost>收益</NButton>
          <NButton size="tiny" :type="sortBy === 'id' ? 'primary' : 'default'" @click="emit('update:sortBy', 'id')" ghost>入驻</NButton>
        </div>
      </div>
    </div>

    <!-- 主播卡片列表 -->
    <div class="user-list">
      <div class="user-card" v-for="v in displayList" :key="v.userName" :class="v.online === true ? 'is-live' : 'is-offline'">
        <span class="card-accent"></span>
        <div class="card-body">
          <!-- 头部：状态点 + 名称 + 直播标签 -->
          <div class="card-head">
            <span class="status-dot" :class="{ live: v.online === true, unknown: v.online == null }"></span>
            <span class="user-name" @click.stop="goDetail(v)">{{ v.userName }}</span>
            <span class="user-nick" v-if="v.nickname && v.nickname !== v.userName">{{ v.nickname }}</span>
            <span class="live-pill" :class="{ live: v.online === true, unknown: v.online == null }">{{ v.online === true ? '直播中' : v.online === false ? '离线' : '未知' }}</span>
          </div>

          <!-- 实时状态：房间 + 时间（未进房以幽灵态融入，不再单独强调） -->
          <div class="card-meta">
            <span class="room" :class="{ ghost: !v.room }">🏠 {{ resolveRoomName(v.room) || '未进房' }}</span>
            <span class="time" v-if="v.online && v.onlineTime">上线 {{ fmtTime(v.onlineTime) }}</span>
            <span class="time" v-else-if="v.lastOfflineTime">离线 {{ fmtTime(v.lastOfflineTime) }}</span>
          </div>

          <!-- 净输赢：Ctrl+Shift+D 统一控制显示/隐藏；负数（亏）红色、正数（赢）绿色，带 +/- 号 -->
          <div class="card-stats" v-if="debugMode">
            <span class="stat-label">净输赢</span>
            <span class="stat-val" :class="netWinColor(v.totalWinAmount)" :title="`累计下注 ${Tools.formatMoney(v.totalBetAmount)}`">
              {{ Tools.formatMoney(v.totalWinAmount, { showSign: true }) }}
            </span>
            <span class="stat-sessions" v-if="v.betCount != null">{{ (v.betCount ?? 0).toLocaleString() }} 场</span>
          </div>

          <!-- 底部：收益 + 操作 -->
          <div class="card-foot">
            <span class="credit" :class="{ pulse: refreshingUser === v.userName }">
              {{ Tools.formatMoney(v.credit) }}
            </span>
            <span class="foot-gap"></span>
            <span v-if="debugMode" class="protect" :class="{ active: v.noClear }" @click.stop="emit('toggleNoClear', v)" title="保护：勾选后不受数据清理影响">{{ v.noClear ? '🔒' : '🔓' }}</span>
            <span class="refresh" :class="{ spinning: refreshingUser === v.userName }" @click.stop="emit('refreshUser', v)" title="刷新收益">⟳</span>
            <NButton size="tiny" type="primary" ghost @click.stop="emit('modifyBalance', v)">调整</NButton>
          </div>
        </div>
      </div>

      <div v-if="displayList.length === 0" class="empty-tip">
        <span class="empty-icon"></span>
        <span>{{ userList.length === 0 ? '暂无主播数据' : '暂无在线主播' }}</span>
      </div>
    </div>

    <!-- 底部分页 -->
    <div class="pagination-bar">
      <NPagination :page="currentPage" @update:page="emit('update:currentPage', $event); emit('pageChange', $event)"
        :page-count="totalPages" :page-slot="7" size="small" />
    </div>

    <!-- 全局刷新悬浮按钮（漂浮在面板右下角）-->
    <span class="global-refresh" :class="{ spinning: refreshingAll }" @click="emit('refreshAll')" title="刷新全部主播收益">⟳</span>
  </div>
</template>

<script setup lang="ts">
import { NButton, NPagination, NSwitch } from 'naive-ui';
import { useRouter } from 'vue-router';
import { inject, ref, computed, type Ref } from 'vue';
import Tools from '@/ts/Tools';
import { resolveRoomName } from '@/ts/roomNameStore';
import { onlyOnline } from '@/ts/listFilterStore';

const router = useRouter();
const debugMode = inject<Ref<boolean>>('debugMode', ref(false));

const props = defineProps<{
  userList: any[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  searchKeyword: string;
  sortBy: string;
  refreshingUser: string | null;
  refreshingAll: boolean;
}>();

// 在线主播优先置顶（仅当前页内重排，不影响分页）：在线(===true)排最前，未知/离线保持服务端原有顺序（稳定排序）。
// “仅显示在线”的过滤已移至后端（GetAccountStatsList 按实时在线状态过滤后再分页），这里不再过滤。
const displayList = computed(() => {
  let arr = (props.userList ?? []).slice();
  return arr
    .map((v: any, i: number) => ({ v, i }))
    .sort((a, b) => {
      const ao = a.v?.online === true ? 1 : 0;
      const bo = b.v?.online === true ? 1 : 0;
      if (ao !== bo) return bo - ao;
      return a.i - b.i;
    })
    .map((x) => x.v);
});

const emit = defineEmits<{
  'update:searchKeyword': [value: string];
  'update:currentPage': [value: number];
  'update:sortBy': [value: string];
  search: [];
  pageChange: [page: number];
  refreshUser: [user: any];
  refreshAll: [];
  modifyBalance: [user: any];
  toggleNoClear: [user: any];
}>();

function goDetail(v: any) {
  router.push({
    name: 'accountDetail',
    params: { userName: v.userName },
    query: {
      online: v.online === true ? '1' : v.online === false ? '0' : '',
      room: v.room || '',
      ot: v.onlineTime || '',
      lo: v.lastOfflineTime || ''
    }
  });
}

// 净输赢配色：正=赢(绿) 负=亏(红) 0/空=灰
function netWinColor(val: number | null | undefined): string {
  if (val == null || val === 0) return 'zero';
  return val > 0 ? 'positive' : 'negative';
}

function fmtTime(v: any): string {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
</script>

<style scoped lang="scss">
.section-card {
  position: relative;
  // 顶部一道金线（背景层实现，随卡片固定不随内容滚动）
  background-image:
    linear-gradient(90deg, transparent, rgba(246,226,178,.55) 22%, rgba(255,246,224,.85) 50%, rgba(246,226,178,.55) 78%, transparent),
    var(--kp-card-bg);
  background-size: 100% 1px, auto;
  background-repeat: no-repeat, no-repeat;
  background-position: top left, center;
  border: 1px solid var(--kp-gold-line-strong);
  border-radius: var(--kp-radius);
  box-shadow: var(--kp-card-shadow);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

// ========== 工具栏 ==========
.toolbar {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;

  .toolbar-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
  }

  .toolbar-left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  .title {
    font-size: 15px;
    font-weight: 700;
    white-space: nowrap;
    letter-spacing: 2px;
    background-image: linear-gradient(180deg, #fdf3da, #d9b773 60%, #b3893f);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    filter: drop-shadow(0 1px 0 rgba(0,0,0,.55));
  }

  .count-badge {
    font-size: 10.5px;
    color: var(--kp-gold-hi);
    background: rgba(216, 178, 95, .10);
    border: 1px solid rgba(216, 178, 95, .20);
    padding: 2px 10px;
    border-radius: 999px;
    font-weight: 600;
    letter-spacing: .5px;
  }

  .online-filter {
    display: flex;
    align-items: center;
    gap: 7px;
    height: 28px;
    padding: 0 10px 0 9px;
    border-radius: 9px;
    background: rgba(255, 240, 210, 0.04);
    border: 1px solid rgba(216, 178, 95, 0.14);
    transition: background .18s, border-color .18s;
    flex-shrink: 0;

    .of-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: var(--kp-faint);
      flex-shrink: 0;
      transition: background .18s, box-shadow .18s;
    }

    .of-label {
      font-size: 11px;
      font-weight: 600;
      color: var(--kp-muted);
      white-space: nowrap;
      transition: color .18s;
    }

    &.on {
      background: rgba(126, 201, 164, 0.10);
      border-color: rgba(126, 201, 164, 0.28);

      .of-dot {
        background: var(--kp-jade);
        box-shadow: 0 0 8px rgba(126, 201, 164, 0.75);
      }

      .of-label {
        color: var(--kp-jade-soft);
      }
    }
  }

  .sort-group {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: 4px;
  }
}

// ========== 底部分页 ==========
.pagination-bar {
  display: flex;
  justify-content: center;
  padding-top: 4px;
  flex-shrink: 0;
}

// ========== 卡片列表 ==========
.user-list {
  flex: 1;
  height: 100%;
  min-height: 0;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  grid-auto-rows: max-content;
  grid-auto-flow: row;
  gap: 12px;
  align-content: start;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(216, 178, 95, 0.22);
    border-radius: 3px;
  }
}

// ========== 主播卡片（重设计：在线/离线强对比，未进房融入状态行）==========
.user-card {
  position: relative;
  background: linear-gradient(160deg, rgba(255,240,210,0.045) 0%, rgba(255,240,210,0.012) 100%);
  border: 1px solid rgba(216,178,95,0.12);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(255,235,198,0.05);
  transition: transform .18s ease, border-color .18s, box-shadow .18s, background .18s;
  min-width: 0;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(216,178,95,0.30);
    box-shadow:
      0 12px 28px -14px #000,
      0 0 26px -14px rgba(216,178,95,0.55);
  }

  // 左侧状态条：在线=香槟金发光，离线=暗金
  .card-accent {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: rgba(216,178,95,0.18);
    transition: background .2s, box-shadow .2s;
  }

  &.is-live {
    border-color: rgba(240,208,138,0.28);
    background: linear-gradient(180deg, rgba(216,178,95,0.12) 0%, rgba(255,240,210,0.02) 55%);

    .card-accent {
      background: linear-gradient(180deg, #fdf0cd, #d8b25f);
      box-shadow: 0 0 12px rgba(240,208,138,0.55);
    }
  }

  &.is-offline {
    .user-name { color: var(--kp-muted); }
    .room { color: var(--kp-faint); }
  }

  .card-body {
    padding: 10px 12px 10px 15px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }

  // 头部：状态点 + 名称 + 直播标签
  .card-head {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
  }

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: var(--kp-faint);
    box-shadow: inset 0 0 0 1px rgba(255,240,210,0.10);

    &.live {
      background: var(--kp-jade);
      box-shadow: 0 0 7px rgba(126,201,164,0.85);
      animation: livePulse 2s ease-in-out infinite;
    }

    &.unknown {
      background: transparent;
      box-shadow: inset 0 0 0 2px var(--kp-gold);
    }
  }

  .user-name {
    font-size: 13px;
    font-weight: 700;
    color: var(--kp-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 92px;
    cursor: pointer;
    transition: color .15s;
    &:hover { color: var(--kp-gold-hi); }
  }

  .user-nick {
    font-size: 10px;
    color: var(--kp-faint);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 54px;
  }

  .live-pill {
    margin-left: auto;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: .6px;
    padding: 2px 8px;
    border-radius: 999px;
    white-space: nowrap;
    flex-shrink: 0;
    color: var(--kp-muted);
    background: rgba(255,240,210,0.04);
    border: 1px solid rgba(216,178,95,0.14);

    &.live {
      color: #08211a;
      background: linear-gradient(135deg, #a9e3c6, #6fbf95);
      border-color: transparent;
      box-shadow: 0 0 14px -6px rgba(126,201,164,0.8);
    }

    &.unknown {
      color: var(--kp-gold-hi);
      background: rgba(216,178,95,0.12);
      border-color: rgba(216,178,95,0.28);
    }
  }

  // 实时状态行：房间 + 时间
  .card-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    min-width: 0;

    .room {
      font-size: 11px;
      font-weight: 600;
      color: var(--kp-gold);
      white-space: nowrap;

      &.ghost {
        color: var(--kp-faint);
        font-weight: 500;
      }
    }

    .time {
      font-size: 9px;
      color: var(--kp-muted);
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }
  }

  // 净输赢行
  .card-stats {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;

    .stat-label {
      font-size: 10px;
      color: var(--kp-faint);
      letter-spacing: .5px;
      flex-shrink: 0;
    }

    .stat-val {
      font-size: 12.5px;
      font-weight: 700;
      font-family: var(--kp-mono);
      letter-spacing: .3px;
      overflow: hidden;
      text-overflow: ellipsis;

      &.positive { color: var(--kp-jade); text-shadow: 0 0 12px rgba(126,201,164,0.28); }
      &.negative { color: var(--kp-bad-soft); text-shadow: 0 0 12px rgba(205,106,83,0.28); }
      &.zero { color: var(--kp-muted); }
    }

    .stat-sessions {
      margin-left: auto;
      font-size: 9px;
      color: var(--kp-faint);
      flex-shrink: 0;
    }
  }

  // 底部：收益 + 操作
  .card-foot {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .credit {
    font-size: 14px;
    font-weight: 700;
    color: var(--kp-gold-hi);
    font-family: var(--kp-mono);
    letter-spacing: 0.4px;
    white-space: nowrap;
    text-shadow: 0 0 14px rgba(216,178,95,0.30);

    &.pulse { animation: pulse 0.8s ease-in-out infinite; }
  }

  .foot-gap { flex: 1; }

  .protect {
    font-size: 13px;
    cursor: pointer;
    opacity: 0.4;
    line-height: 1;
    flex-shrink: 0;
    &:hover { opacity: 0.8; }
    &.active { opacity: 0.9; color: var(--kp-gold); }
  }

  .refresh {
    font-size: 13px;
    color: rgba(216,178,95,0.42);
    cursor: pointer;
    line-height: 1;
    flex-shrink: 0;
    transition: color .15s;
    &:hover { color: var(--kp-gold-hi); }
    &.spinning {
      animation: spin 0.8s linear infinite;
      color: var(--kp-gold-hi);
    }
  }
}

// ========== 空状态 ==========
.empty-tip {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 60px 40px;
  color: var(--kp-faint);
  font-size: 13px;
  letter-spacing: 1px;
  border: 1px dashed rgba(216,178,95,0.16);
  border-radius: 12px;
  background: rgba(255,240,210,0.015);

  .empty-icon {
    width: 10px;
    height: 10px;
    transform: rotate(45deg);
    background: linear-gradient(135deg, #fdf3da, #c39a4f);
    box-shadow: 0 0 14px 0 rgba(240,208,138,0.55);
    animation: kp-diamond-twinkle 2.4s ease-in-out infinite;
  }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

// ========== 全局刷新悬浮按钮（漂浮在面板右下角）==========
.global-refresh {
  position: absolute;
  bottom: 5px;
  right: 24px;
  z-index: 10;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
  color: var(--kp-gold);
  background: linear-gradient(180deg, rgba(216,178,95,0.22), rgba(168,124,57,0.10));
  border: 1px solid rgba(216,178,95,0.34);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.25s;
  backdrop-filter: blur(6px);
  box-shadow:
    0 6px 20px -10px #000,
    0 0 22px -12px rgba(240,208,138,0.65);

  &:hover {
    color: #fff6e0;
    background: linear-gradient(180deg, rgba(216,178,95,0.34), rgba(168,124,57,0.16));
    border-color: rgba(246,227,182,0.60);
    box-shadow:
      0 6px 24px -10px #000,
      0 0 28px -10px rgba(240,208,138,0.85);
    transform: scale(1.08);
  }

  &.spinning {
    animation: spin 0.8s linear infinite;
    color: #fff6e0;
    background: rgba(216,178,95,0.34);
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

@keyframes livePulse {
  0%, 100% { box-shadow: 0 0 4px rgba(126, 201, 164, 0.40); }
  50% { box-shadow: 0 0 12px rgba(126, 201, 164, 0.80); }
}
</style>
